<script setup lang="ts">
const { path } = useRoute();
const { isDesktop } = useLayout();

const newsResponse = ref<NewsListResponse | null>(null);
const paginationLoadingStates = ref<PaginationLoadingObject[]>([]);
const defaultNewsListPageSize = 8;
const { tags } = useFilters();
const paginationDefaults = usePaginationDefaults();

const { fetchPage, handlePaginationUpdate } = usePaginatedList<NewsListItem>({
  path,
  dataRef: newsResponse,
  pageSize: defaultNewsListPageSize,
  loadingStatusRef: paginationLoadingStates
});

const { initialPage, initialPageSize } = usePaginationInitialState({
  defaultPageSize: defaultNewsListPageSize,
  initialPage: 1,
  initialPageSize: defaultNewsListPageSize
});

await fetchPage({
  page: initialPage,
  size: initialPageSize,
  filters: {
    tags
  },
  loadingType: 'initial'
});

const dynamicPageNumber = computed(() => {
  return newsResponse.value?.pageNumber ?? initialPage;
});

const dynamicPageSize = computed(() => {
  return newsResponse.value?.pageSize ?? initialPageSize;
});

const dynamicTotalItems = computed(() => {
  if (!newsResponse.value) return 0;

  return newsResponse.value.totalItems;
});

const { setParams, removeParams } = useQueryParams();

async function handleFilterUpdate(tag?: string) {
  const newTags = tag ? [tag] : [];

  if (newTags.length) {
    setParams({ tags: newTags });
  } else {
    removeParams(['tags', paginationDefaults.pageQueryKey]);
  }
  await fetchPage({
    page: 1,
    size: dynamicPageSize.value,
    filters: {
      tags: tag
    },
    loadingType: 'filter'
  });
}
</script>

<template>
  <section
    v-if="newsResponse?.items?.length"
    class="grid gap-6 lg:gap-10"
  >
    <PartialsNewsListFilter
      v-if="newsResponse.filters.tags && newsResponse.filters.tags.length > 0"
      :filters="newsResponse.filters.tags"
      @update:filter="handleFilterUpdate"
    />
    <div class="grid grid-flow-row gap-16">
      <PaginationProvider
        provider-key="news-list"
        :show-first-and-last-buttons="isDesktop"
        :lock-first-and-last-pages="!isDesktop"
        :initial-page="dynamicPageNumber"
        :total-items="dynamicTotalItems"
        :max-visible-page-buttons="3"
        :show-page-size-change="false"
        :default-page-size="defaultNewsListPageSize"
        :item-type="$t('Pagination.Aria.ListItemType.News')"
        :item-type-plural="$t('Pagination.Aria.ListItemTypePlural.News')"
        :pagination-id="$t('Pagination.Id.NewsList')"
        :loading-status="paginationLoadingStates"
        @update:page="(payload) => handlePaginationUpdate(payload, 'page')"
      >
        <PaginatedList
          :loading="
            checkPaginatedLoadingStates({
              loadingStates: paginationLoadingStates,
              typesToCheck: ['page']
            })
          "
          transition-name="list"
          class="mb-8 grid gap-8 sm:grid-cols-2 lg:mb-12 lg:grid-cols-3 2xl:grid-cols-4"
          :list-aria-label="$t('Aria.NewsListPage.NewsList')"
        >
          <li
            v-for="(newsItem, index) in newsResponse?.items"
            :key="`${newsItem.headline}-${index}`"
            class="flex items-center justify-center"
          >
            <PartialsNewsListItem :news-item="newsItem" />
          </li>
        </PaginatedList>
      </PaginationProvider>
    </div>
  </section>
</template>
